import api from './api';
import { ApiResponse, unwrapApiResponse } from '../utils/api-response.util';
import { Locale } from './workout.service';
import {
  MetActivity,
  MetActivityEdit,
  MetActivityOptions,
  CreateMetActivityDto,
  GetMetActivitiesParams,
  PaginatedMetActivitiesResponse,
} from './met-activity.service';

export interface MetActivityDetail {
  id: number;
  code: string;
  compendiumType: 'adult' | 'youth' | 'elderly';
  categoryCode: number;
  categoryName: string;
  nameEn: string;
  nameI18n: { [key in Locale]?: string } | null;
  metValue: number;
  youthAgeGroup: number | null;
  youthAgeGroupLabel: string | null;
  keywords: string | null;
  createdAt: string;
  updatedAt: string;
}

/** actual - вектор з поточного тексту; stale - текст змінився; missing - нема; not_indexed - не Compendium / не adult. */
export type MetIndexRowStatus = 'actual' | 'stale' | 'missing' | 'not_indexed';

/** Перебудова AI-індексу MET іде у фоні на сервері - стан для прогресу. */
export interface MetIndexBuildState {
  running: boolean;
  startedAt: string | null;
  finishedAt: string | null;
  total: number;
  processed: number;
  built: number;
  unchanged: number;
  failed: number;
  error: string | null;
}

class AdminMetActivityService {
  private readonly BASE_URL = '/admin/lib/met-activities';

  async getOptions(): Promise<MetActivityOptions> {
    const response = await api.get<ApiResponse<MetActivityOptions>>(`${this.BASE_URL}/options`);
    return unwrapApiResponse<MetActivityOptions>(response);
  }

  async getActivities(params: GetMetActivitiesParams): Promise<PaginatedMetActivitiesResponse> {
    const response = await api.get<any>(`${this.BASE_URL}`, {
      params,
    });
    const body = response.data;
    if (body && body.success && Array.isArray(body.data)) {
      return { data: body.data, meta: body.meta };
    }
    console.error('Unexpected API response for admin getMetActivities:', body);
    return { data: [], meta: { total: 0, page: 1, limit: params.limit || 20 } };
  }

  async getActivityForEdit(id: number): Promise<MetActivityEdit> {
    const response = await api.get<ApiResponse<MetActivityEdit>>(`${this.BASE_URL}/${id}/edit`);
    return unwrapApiResponse<MetActivityEdit>(response);
  }

  async getActivityDetail(id: number): Promise<MetActivityDetail> {
    const response = await api.get<ApiResponse<MetActivityDetail>>(`${this.BASE_URL}/${id}/detail`);
    return unwrapApiResponse<MetActivityDetail>(response);
  }

  async createActivity(payload: CreateMetActivityDto): Promise<MetActivity> {
    const response = await api.post<ApiResponse<MetActivity>>(`${this.BASE_URL}`, payload);
    return unwrapApiResponse<MetActivity>(response);
  }

  /** Оновити AI-індекс бази MET (лише нові й змінені записи) - щоб AI бачив нові переклади й ключові фрази. */
  async rebuildAiIndex(): Promise<{ started: boolean; build: MetIndexBuildState }> {
    const response = await api.post('/admin/ai-cache/workouts/rebuild-met-embeddings');
    return response.data.data;
  }

  /** Статус AI-індексу рядків (id з поточної сторінки). */
  async getAiIndexStatus(ids: number[]): Promise<Record<number, MetIndexRowStatus>> {
    if (!ids.length) return {};
    const response = await api.get('/admin/ai-cache/workouts/met-embedding-status', { params: { ids: ids.join(',') } });
    return response.data.data ?? {};
  }

  /** Перерахувати вектор одного запису зараз; повертає новий статус. */
  async rebuildAiIndexOne(id: number): Promise<MetIndexRowStatus> {
    const response = await api.post(`/admin/ai-cache/workouts/met-embeddings/${id}/rebuild`);
    return response.data.data?.status;
  }

  /** Стан AI-індексу MET: кількість записів і прогрес поточної / останньої перебудови. */
  async getAiIndexStats(): Promise<{ indexedCount: number; build: MetIndexBuildState }> {
    const response = await api.get('/admin/ai-cache/workouts/met-embedding-stats');
    return response.data.data;
  }

  /** Лише ключові фрази (одна на рядок); наукові поля Compendium не змінюються. */
  async updateKeywords(id: number, keywords: string[]): Promise<MetActivity> {
    const response = await api.patch<ApiResponse<MetActivity>>(`${this.BASE_URL}/${id}/keywords`, {
      keywords: keywords.join('\n'),
    });
    return unwrapApiResponse<MetActivity>(response);
  }

  async updateActivity(id: number, payload: Partial<CreateMetActivityDto>): Promise<MetActivity> {
    const response = await api.patch<ApiResponse<MetActivity>>(`${this.BASE_URL}/${id}`, payload);
    return unwrapApiResponse<MetActivity>(response);
  }

  async deleteActivity(id: number): Promise<void> {
    await api.delete<ApiResponse<void>>(`${this.BASE_URL}/${id}`);
  }
}

export const adminMetActivityService = new AdminMetActivityService();
export type {
  MetActivity,
  MetActivityEdit,
  MetActivityOptions,
  CreateMetActivityDto,
  GetMetActivitiesParams,
  PaginatedMetActivitiesResponse,
};
